<template>
  <div class="official-account-category">
    <div class="section-header">
      <div class="section-icon">📚</div>
      <h2 class="section-title">公众号分类</h2>
    </div>
    
    <!-- 一级分类标签 -->
    <div class="primary-categories">
      <div 
        v-for="label in primaryLabels" 
        :key="label.id"
        class="category-tab"
        :class="{ active: selectedLabelId === label.id }"
        @click="selectPrimaryLabel(label.id)"
      >
        {{ label.name }}
      </div>
    </div>
    
    <!-- 二级分类 -->
    <div class="secondary-categories" v-if="secondaryCategories.length > 0">
      <div class="category-list">
        <div 
          class="category-item"
          :class="{ active: selectedCategoryId === null }"
          @click="selectSecondaryCategory(null)"
        >
          全部
        </div>
        <div 
          v-for="category in secondaryCategories" 
          :key="category.id"
          class="category-item"
          :class="{ active: selectedCategoryId === category.id }"
          @click="selectSecondaryCategory(category.id)"
        >
          {{ category.name }}
        </div>
      </div>
    </div>
    
    <!-- 公众号列表 -->
    <div class="account-list">
      <div v-if="loading" class="loading">
        <div class="loading-spinner"></div>
        <span>加载中...</span>
      </div>
      
      <div v-else-if="errorMessage" class="error-state">
        <div class="error-icon">⚠️</div>
        <p>{{ errorMessage }}</p>
        <button class="retry-btn" @click="retryLoadData">重试</button>
      </div>
      
      <div v-else-if="groupedAccounts && Object.keys(groupedAccounts).length > 0" class="account-groups">
        <div v-for="(accounts, initial) in groupedAccounts" :key="initial" class="initial-group">
          <div class="initial-header">{{ initial }}</div>
          <div 
            v-for="account in accounts" 
            :key="account.id" 
            class="account-item"
            @click="viewAccount(account)"
          >
            <div class="account-avatar">
              <img 
                v-if="account.officialaccount_image" 
                :src="getImageUrl(account.officialaccount_image)" 
                :alt="account.name"
                @error="handleImageError"
                @load="handleImageLoad"
              />
              <div v-else class="default-avatar">{{ account.name.charAt(0) }}</div>
            </div>
            <div class="account-info">
              <div class="account-header">
                <div class="account-name">{{ account.name }}</div>
                <div class="account-right-info">
                  <div class="score" v-if="account.score">
                    <span class="score-value">{{ account.score.total_average_score }}</span>
                    <div class="stars">
                      <span 
                        v-for="n in 5" 
                        :key="n" 
                        class="star"
                        :class="{ filled: n <= Math.floor(account.score.total_average_star) }"
                      >★</span>
                    </div>
                  </div>
                  <div class="comment-count" v-if="account.commentNum">
                    <span class="count-badge">{{ account.commentNum }}</span>
                  </div>
                </div>
              </div>
              <div class="account-categories" v-if="account.category && account.category.length > 0">
                <span 
                  v-for="cat in account.category.slice(0, 2)" 
                  :key="cat.id" 
                  class="category-tag"
                >
                  {{ cat.name }}
                </span>
              </div>
            </div>
          </div>
        </div>
    </div>
      
      <div v-else class="empty-state">
        <div class="empty-icon">📭</div>
        <p>暂无公众号数据</p>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  getOfficialAccountLabelList, 
  getOfficialAccountCategoryList, 
  getOfficialAccountList 
} from '@/http/api/news'

export default {
  name: 'OfficialAccountCategory',
  data() {
    return {
      primaryLabels: [],
      secondaryCategories: [],
      accountList: [],
      allCategoriesData: [], // 存储所有二级分类的完整数据
      selectedLabelId: null,
      selectedCategoryId: null,
      loading: false,
      errorMessage: ''
    }
  },
  computed: {
    // 按首字母分组的公众号列表
    groupedAccounts() {
      if (!this.accountList || this.accountList.length === 0) {
        return {}
      }
      
      const groups = {}
      this.accountList.forEach(account => {
        const initial = account.initial || account.name.charAt(0).toUpperCase()
        if (!groups[initial]) {
          groups[initial] = []
        }
        groups[initial].push(account)
      })
      
      // 按字母顺序排序
      const sortedGroups = {}
      Object.keys(groups).sort().forEach(key => {
        sortedGroups[key] = groups[key]
      })
      
      return sortedGroups
    }
  },
  async mounted() {
    // 1. 首先加载一级分类标签
    await this.loadPrimaryLabels()
    
    // 2. 如果有一级分类，默认选中第一个并加载其数据
    if (this.primaryLabels.length > 0) {
      // 直接调用选择一级分类的方法，它会处理所有逻辑
      await this.selectPrimaryLabel(this.primaryLabels[0].id)
    } else {
      // 如果没有一级分类，清空数据
      this.secondaryCategories = []
      this.allCategoriesData = []
      this.accountList = []
    }
  },
  methods: {
    // 加载一级分类标签
    async loadPrimaryLabels() {
      try {
        const response = await getOfficialAccountLabelList()
        
        if (response && response.code === 200 && response.result) {
          this.primaryLabels = response.result
        } else {
          this.primaryLabels = []
        }
        
        // 设置默认选中第一个分类
        if (this.primaryLabels.length > 0 && !this.selectedLabelId) {
          this.selectedLabelId = this.primaryLabels[0].id
        }
      } catch (error) {
        this.primaryLabels = []
      }
    },
    
    // 加载二级分类
    async loadSecondaryCategories(labelId = null) {
      try {
        const params = labelId ? { label_id: labelId } : {}
        const response = await getOfficialAccountCategoryList(params)
        
        if (response && response.code === 200 && response.result) {
          this.secondaryCategories = response.result
        } else {
          this.secondaryCategories = []
        }
      } catch (error) {
        this.secondaryCategories = []
      }
    },
    
    // 加载所有二级分类的数据并组合（用于初始化和重试）
    async loadAllCategoriesData(labelId) {
      // 直接调用选择一级分类的逻辑
      await this.selectPrimaryLabel(labelId)
    },
    
    // 获取指定二级分类的公众号数据
    async getAccountsByCategoryId(categoryId, labelId) {
      try {
        const params = {
          member_id: 444,
          page: 1,
          limit: 1000,
          category_id: categoryId,
          label_id: labelId
        }
        
        const response = await getOfficialAccountList(params)
        if (response && response.code === 200 && response.result) {
          return response.result
        }
        return []
      } catch (error) {
        return []
      }
    },
    
    // 加载公众号列表
    async loadAccountList(categoryId = null, labelId = null) {
      this.loading = true
      this.errorMessage = ''
      try {
        const params = {
          member_id: 444,
          page: 1,
          limit: 1000
        }
        
        // 一级分类ID必须传递
        if (labelId) {
          params.label_id = labelId
        }
        
        // 二级分类ID：只有明确选择了具体分类才传递，"全部"时不传递
        if (categoryId !== null && categoryId !== undefined) {
          params.category_id = categoryId
        }
        
        const response = await getOfficialAccountList(params)
        if (response && response.code === 200 && response.result) {
          this.accountList = response.result
          this.errorMessage = ''
        } else {
          this.accountList = []
          if (response && response.message) {
            this.errorMessage = `加载失败: ${response.message}`
          } else {
            this.errorMessage = '获取公众号列表失败，请稍后重试'
          }
        }
      } catch (error) {
        this.accountList = []
        this.errorMessage = '网络错误，请检查网络连接后重试'
      } finally {
        this.loading = false
      }
    },
    
    // 选择一级分类
    async selectPrimaryLabel(labelId) {
      console.log('选择一级分类', labelId)
      this.selectedLabelId = labelId
      this.selectedCategoryId = null // 重置为"全部"
      
      this.loading = true
      this.errorMessage = ''
      
      try {
        // 1. 先加载二级分类列表
        await this.loadSecondaryCategories(labelId)
        
        // 2. 然后遍历所有二级分类，组合数据
        if (this.secondaryCategories && this.secondaryCategories.length > 0) {
          const allData = []
          const accountIds = new Set() // 用于去重
          
          // 并行请求所有二级分类的数据
          const promises = this.secondaryCategories.map(category => 
            this.getAccountsByCategoryId(category.id, labelId)
          )
          
          const results = await Promise.allSettled(promises)
          
          // 合并所有成功的结果
          results.forEach(result => {
            if (result.status === 'fulfilled' && result.value) {
              result.value.forEach(account => {
                // 去重：同一个公众号可能属于多个分类
                if (!accountIds.has(account.id)) {
                  accountIds.add(account.id)
                  allData.push(account)
                }
              })
            }
          })
          
          // 更新全部分类数据
          this.allCategoriesData = allData
          
          // 由于重置为"全部"状态（selectedCategoryId = null），显示组合后的全部数据
          this.accountList = [...allData] // 使用扩展运算符确保响应式更新
          
          console.log('一级分类切换完成，全部数据条数:', allData.length)
        } else {
          this.allCategoriesData = []
          this.accountList = []
        }
        
        this.errorMessage = ''
      } catch (error) {
        console.error('加载一级分类数据失败:', error)
        this.allCategoriesData = []
        this.accountList = []
        this.errorMessage = '加载数据失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    
    // 选择二级分类
    async selectSecondaryCategory(categoryId) {
      this.selectedCategoryId = categoryId
      
      if (categoryId === null) {
        // 选择"全部"：显示组合的全部数据
        this.accountList = [...this.allCategoriesData] // 使用扩展运算符确保响应式更新
        console.log('切换到全部，显示数据条数:', this.allCategoriesData.length)
      } else {
        // 选择具体二级分类：加载该分类的数据
        console.log('切换到具体分类:', categoryId)
        await this.loadAccountList(categoryId, this.selectedLabelId)
      }
    },
    
    // 查看公众号详情
    viewAccount(account) {
      // 这里可以添加跳转到公众号详情页的逻辑
    },
    
    // 获取图片URL
    getImageUrl(imagePath) {
      if (!imagePath) return ''
      
      // 如果已经是完整的URL，直接返回
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath
      }
      
      // 处理相对路径，使用OSS基础URL
      // 确保路径以 / 开头
      const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`
      
      // 使用书客网的OSS基础URL
      const ossBaseUrl = 'https://oss.bookor.com.cn'
      
      // 如果路径不是以 /uploads 开头，添加它
      const finalPath = cleanPath.startsWith('/uploads') ? cleanPath : `/uploads${cleanPath}`
      
      const fullUrl = `${ossBaseUrl}${finalPath}`
      console.log('图片URL转换:', imagePath, '->', fullUrl)
      
      return fullUrl
    },
    
    // 处理图片加载成功
    handleImageLoad(event) {
      console.log('图片加载成功:', event.target.src)
    },
    
    // 处理图片加载错误
    handleImageError(event) {
      console.log('图片加载失败:', event.target.src)
      // 隐藏失败的图片，这样会显示默认头像
      event.target.style.display = 'none'
      
      // 可以尝试使用其他后备图片
      // event.target.src = 'https://oss.bookor.com.cn/uploads/default-avatar.png'
    },
    
    // 重试加载数据
    async retryLoadData() {
      this.errorMessage = ''
      
      // 重新执行完整的初始化流程
      await this.loadPrimaryLabels()
      
      if (this.primaryLabels.length > 0) {
        if (!this.selectedLabelId) {
          this.selectedLabelId = this.primaryLabels[0].id
        }
        await this.loadSecondaryCategories(this.selectedLabelId)
        
        if (this.selectedCategoryId === null) {
          // 如果当前选中"全部"，重新加载组合数据
          await this.loadAllCategoriesData(this.selectedLabelId)
        } else {
          // 如果选中具体分类，加载该分类数据
          await this.loadAccountList(this.selectedCategoryId, this.selectedLabelId)
        }
      } else {
        this.secondaryCategories = []
        this.allCategoriesData = []
        this.accountList = []
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.official-account-category {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
  
  .section-icon {
    font-size: 18px;
    margin-right: 8px;
    color: #3498db;
  }
  
  .section-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    color: #333;
  }
}

// 一级分类标签
.primary-categories {
  display: flex;
  flex-wrap: wrap;
  padding: 10px 16px;
  gap: 8px;
  background-color: #d1e8ff;
  border-bottom: 1px solid #2980b9;
  
  .category-tab {
    padding: 4px 10px;
    background: rgba(255, 255, 255, 0.9);
    border: 2px solid transparent;
    border-radius: 20px;
    font-size: 12px;
    color: #2c3e50;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
    &:hover {
      background: #fff;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      border-color: rgba(255, 255, 255, 0.8);
    }
    
    &.active {
      background: #fff;
      color: #3498db;
      border-color: #fff;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
      transform: translateY(-2px);
    }
  }
}

// 二级分类
.secondary-categories {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  
  .category-list {
    display: flex;
    flex-wrap: wrap;
    padding: 8px 16px;
    gap: 4px;
    
    .category-item {
      padding: 2px 6px;
      background: #fff;
      border: 1px solid #dee2e6;
      border-radius: 12px;
      font-size: 11px;
      color: #6c757d;
      cursor: pointer;
      transition: all 0.2s ease;
      font-weight: 500;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      
      &:hover {
        background: #e9ecef;
        border-color: #adb5bd;
        color: #495057;
      }
      
      &.active {
        background: #27ae60;
        color: white;
        border-color: #27ae60;
        box-shadow: 0 1px 4px rgba(39, 174, 96, 0.4);
        font-weight: 600;
      }
    }
  }
}

// 公众号列表
.account-list {
  flex: 1;
  padding: 20px 16px;
  overflow-y: auto;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #e9ecef;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #adb5bd, #6c757d);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #6c757d, #495057);
  }
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
  font-size: 14px;
  
  .loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 8px;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.account-groups {
  .initial-group {
    margin-bottom: 16px;
    
    .initial-header {
      font-size: 12px;
      font-weight: 500;
      color: #666;
      padding: 4px 0 2px;
      margin-bottom: 6px;
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 20px;
        height: 1px;
        background: #ddd;
      }
    }
  }
}

.account-item {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  margin-bottom: 8px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.15);
    border-color: #3498db;
    transform: translateY(-1px);
  }
  
  .account-avatar {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    overflow: hidden;
    margin-right: 12px;
    flex-shrink: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .default-avatar {
      width: 100%;
      height: 100%;
      background-color: #96befd;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      font-weight: 600;
    }
  }
  
  .account-info {
    flex: 1;
    min-width: 0;
    
    .account-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 6px;
      
      .account-name {
        font-size: 14px;
        font-weight: 600;
        color: #2c3e50;
  flex: 1;
        margin-right: 8px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      
      .account-right-info {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-shrink: 0;
        
        .score {
          display: flex;
          align-items: center;
          gap: 3px;
          background: #fff8f0;
          padding: 2px 6px;
          border-radius: 12px;
          border: 1px solid #ffeaa7;
          
          .score-value {
            font-size: 11px;
            color: #f39c12;
            font-weight: 700;
          }
          
          .stars {
            display: flex;
            gap: 1px;
            
            .star {
              font-size: 9px;
              color: #ddd;
              
              &.filled {
                color: #f39c12;
              }
            }
          }
        }
        
        .comment-count {
          .count-badge {
            background: linear-gradient(135deg, #e74c3c, #c0392b);
            color: white;
            font-size: 9px;
            padding: 2px 5px;
            border-radius: 10px;
            min-width: 16px;
            text-align: center;
            font-weight: 600;
            box-shadow: 0 1px 3px rgba(231, 76, 60, 0.3);
          }
        }
      }
    }
    
    .account-categories {
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
      
      .category-tag {
        background: linear-gradient(135deg, #f8f9fa, #e9ecef);
        color: #495057;
        font-size: 10px;
        padding: 3px 8px;
        border-radius: 12px;
        border: 1px solid #dee2e6;
        font-weight: 500;
      }
    }
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #999;
  text-align: center;
  
  .empty-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }
  
  p {
    font-size: 14px;
    margin: 0;
  }
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #e74c3c;
  text-align: center;
  
  .error-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }
  
  p {
    font-size: 14px;
    margin: 0 0 16px 0;
    color: #666;
  }
  
  .retry-btn {
    background: #3498db;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.3s ease;
    
    &:hover {
      background: #2980b9;
    }
    
    &:active {
      transform: translateY(1px);
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .primary-categories {
    padding: 8px 12px;
    gap: 6px;
    
    .category-tab {
      font-size: 13px;
      padding: 6px 14px;
    }
  }
  
  .secondary-categories .category-list {
    padding: 6px 12px;
    gap: 4px;
    
    .category-item {
      font-size: 10px;
      padding: 3px 8px;
    }
  }
  
  .account-list {
    padding: 16px 12px;
  }
  
  .account-item {
    padding: 10px;
    
    .account-avatar {
      width: 36px;
      height: 36px;
      margin-right: 10px;
      
      .default-avatar {
    font-size: 14px;
      }
    }
    
    .account-info {
      .account-header {
        .account-name {
          font-size: 13px;
        }
        
        .account-right-info {
          gap: 4px;
          
          .score {
            padding: 2px 4px;
            
            .score-value {
              font-size: 10px;
            }
            
            .stars .star {
              font-size: 8px;
            }
          }
          
          .comment-count .count-badge {
            font-size: 8px;
            padding: 1px 3px;
            min-width: 14px;
          }
        }
      }
      
      .account-categories .category-tag {
        font-size: 9px;
        padding: 2px 6px;
      }
    }
  }
}
</style>
